import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  KeyRound,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { ConfirmModal } from "../components/ui/ConfirmModal";
import {
  agentsApi,
  type AgentDetail,
  type AgentDetailUpdatePayload,
} from "../api/agentsApi";
import { formatPhoneNumber } from "../lib/phone";
import type { IdentityNumber } from "../api/agentsApi";
import { voicesApi, voiceAudioUrl, type VoiceItem } from "../api/voicesApi";
import { CLI_OPTIONS, STATUS_OPTIONS } from "../lib/destinationTransfer";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; detail: AgentDetail }
  | { status: "error"; message: string };

interface Editable {
  agentName: string;
  emailAddress: string;
  username: string;
  statusId: number;
  destination: string;
  dialTimeout: number;
  cliTypeId: number;
  voicemailEnabled: boolean;
  voicemailSendEmail: boolean;
  maxChannels: number;
  recordCalls: boolean;
  muteWhisper: boolean;
  agentSignoffSMS: string;
  pbxEnabled: boolean;
  pbxIdentityMode: "hidden" | "number" | "from" | "denied";
  pbxIdentityDnisId: number | null;
  /** 4-digit extension number; "" = none. */
  pbxExtension: string;
  /** Voicemail greeting (customer voice); null = system prompt. */
  voicemailVoiceId: number | null;
}

function toEditable(d: AgentDetail): Editable {
  return {
    agentName: d.agentName,
    emailAddress: d.emailAddress,
    username: d.username,
    statusId: d.statusId,
    destination: d.destination,
    dialTimeout: d.dialTimeout,
    cliTypeId: d.cliTypeId,
    voicemailEnabled: d.voicemailEnabled,
    voicemailSendEmail: d.voicemailSendEmail,
    maxChannels: d.maxChannels,
    recordCalls: d.recordCalls,
    muteWhisper: d.muteWhisper,
    agentSignoffSMS: d.agentSignoffSMS,
    pbxEnabled: d.pbxEnabled,
    pbxIdentityMode: d.pbxIdentityMode === "number" || d.pbxIdentityMode === "hidden" || d.pbxIdentityMode === "from" ? d.pbxIdentityMode : "denied",
    pbxIdentityDnisId: d.pbxIdentityDnisId,
    pbxExtension: d.pbxExtension ?? "",
    voicemailVoiceId: d.voicemailVoiceId ?? null,
  };
}

function toPayload(e: Editable, supported: boolean): AgentDetailUpdatePayload {
  return {
    agentName: e.agentName,
    emailAddress: e.emailAddress,
    username: e.username,
    statusId: e.statusId,
    destination: e.destination,
    dialTimeout: e.dialTimeout,
    cliTypeId: e.cliTypeId,
    voicemailEnabled: e.voicemailEnabled,
    voicemailSendEmail: e.voicemailSendEmail,
    maxChannels: e.maxChannels,
    recordCalls: e.recordCalls,
    muteWhisper: e.muteWhisper,
    agentSignoffSMS: e.agentSignoffSMS,
    pbxEnabled: e.pbxEnabled,
    pbxIdentityMode: e.pbxIdentityMode,
    pbxIdentityDnisId: e.pbxIdentityMode !== "denied" ? e.pbxIdentityDnisId : null,
    // Only sent when the database has the columns (script 20); null = keep.
    pbxExtension: supported ? e.pbxExtension.trim() : null,
    setVoicemailVoice: supported,
    voicemailVoiceId: supported ? e.voicemailVoiceId : null,
  };
}

/**
 * Restricted destination detail editor for aivrmanager. Panels are rendered
 * in this order:
 *
 *   - General:    name
 *   - Phone:      status, primary number, available numbers, attempt
 *                 timeout, CLI type, max channels, mute whisper
 *   - Voicemail:  enabled flag, notification (send email)
 *   - Recordings: record calls
 *   - PBX:        (when pbxEnabled) read-only username/password/registration
 *   - Contact:    email, mobile (SMS sign-off)
 *   - Login:      username + change-password modal
 *   - Archive:    archive button
 *
 * Anything not in this list is preserved by the backend on save.
 */
export function DestinationDetailPage() {
  const { destinationId } = useParams<{ destinationId: string }>();
  const navigate = useNavigate();
  const id = Number(destinationId);

  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [editable, setEditable] = useState<Editable | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [pwOpen, setPwOpen] = useState(false);
  const [destOpen, setDestOpen] = useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  /** Customer's active numbers for the PBX identity (loaded once PBX is in play). */
  const [identityNumbers, setIdentityNumbers] = useState<IdentityNumber[] | null>(null);
  useEffect(() => {
    if (state.status !== "ready") return;
    const d = state.detail;
    if (!(d.customerPbxEnabled || d.pbxEnabled) || !d.pbxIdentitySupported || identityNumbers) return;
    agentsApi.identityNumbers().then(setIdentityNumbers).catch(() => setIdentityNumbers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  /** Customer voices for the voicemail greeting (loaded once the greeting can be set). */
  const [voices, setVoices] = useState<VoiceItem[] | null>(null);
  useEffect(() => {
    if (state.status !== "ready" || voices) return;
    if (!state.detail.internalCallsSupported) return;
    voicesApi.list(null).then((r) => setVoices(r.items.filter((v) => v.hasVoiceFile))).catch(() => setVoices([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    let active = true;
    setState({ status: "loading" });
    agentsApi
      .get(id)
      .then((detail) => {
        if (!active) return;
        setState({ status: "ready", detail });
        setEditable(toEditable(detail));
      })
      .catch((err: unknown) => {
        if (!active) return;
        const message =
          err instanceof Error ? err.message : "Failed to load destination";
        setState({ status: "error", message });
      });
    return () => {
      active = false;
    };
  }, [id]);

  const isDirty = useMemo(() => {
    if (state.status !== "ready" || !editable) return false;
    const a = toEditable(state.detail);
    return JSON.stringify(a) !== JSON.stringify(editable);
  }, [state, editable]);

  const flash = (msg: string) => {
    setSuccess(msg);
    window.setTimeout(() => setSuccess(null), 2200);
  };

  const IDENTITY_ERRORS: Record<string, string> = {
    identity_number_required: "Choose a number for the PBX identity (with Hidden it is only used for billing, with Use From it is the fallback).",
    identity_number_invalid: "The chosen identity number is not an active number of this customer.",
    identity_mode_invalid: "Unknown identity mode.",
    extension_invalid: "The extension number must be exactly 4 digits.",
    extension_in_use: "That extension number is already used by another destination of this customer.",
    voicemail_voice_invalid: "The chosen voicemail greeting is not one of this customer's voices.",
  };

  const onSave = async () => {
    if (!editable) return;
    if (editable.pbxEnabled && editable.pbxIdentityMode !== "denied" && editable.pbxIdentityDnisId == null) {
      setError(IDENTITY_ERRORS.identity_number_required);
      return;
    }
    const ext = editable.pbxExtension.trim();
    if (ext.length > 0 && !/^[0-9]{4}$/.test(ext)) {
      setError(IDENTITY_ERRORS.extension_invalid);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await agentsApi.update(id, toPayload(editable, state.status === "ready" && state.detail.internalCallsSupported));
      setState({ status: "ready", detail: saved });
      setEditable(toEditable(saved));
      flash("Saved");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Save failed";
      setError(IDENTITY_ERRORS[msg] ?? msg);
    } finally {
      setSaving(false);
    }
  };

  const performArchive = async () => {
    setArchiveConfirmOpen(false);
    setArchiving(true);
    setError(null);
    try {
      await agentsApi.archive(id);
      navigate("/destinations");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Archive failed");
      setArchiving(false);
    }
  };

  const onAddDestination = async (destination: string, description: string) => {
    try {
      await agentsApi.addDestination(id, { destination, description });
      const next = await agentsApi.get(id);
      setState({ status: "ready", detail: next });
      setEditable(toEditable(next));
      setDestOpen(false);
      flash("Number added");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Add failed");
    }
  };

  const onDeleteDestination = async (agentDestinationListId: number) => {
    try {
      await agentsApi.deleteDestination(id, agentDestinationListId);
      const next = await agentsApi.get(id);
      setState({ status: "ready", detail: next });
      setEditable(toEditable(next));
      flash("Number removed");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    }
  };

  const onActivateDestination = async (agentDestinationListId: number) => {
    try {
      const next = await agentsApi.activateDestination(
        id,
        agentDestinationListId,
      );
      setState({ status: "ready", detail: next });
      setEditable(toEditable(next));
      flash("Number activated");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Activate failed");
    }
  };

  const onChangePassword = async (newPassword: string) => {
    try {
      await agentsApi.changePassword(id, newPassword);
      setPwOpen(false);
      flash("Password changed");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Password change failed");
    }
  };

  if (state.status === "loading" || !editable) {
    return (
      <>
        <PageHeader title="Destination" />
        <div className="dashboard">
          <div className="tile" style={{ color: "var(--portal-muted)" }}>
            Loading…
          </div>
        </div>
      </>
    );
  }

  if (state.status === "error") {
    return (
      <>
        <PageHeader title="Destination" />
        <div className="dashboard">
          <div
            className="tile"
            style={{
              borderColor: "#e74c3c",
              color: "#c0392b",
              background: "#fdecea",
            }}
          >
            <strong>Something went wrong.</strong>
            <div style={{ marginTop: 6, fontSize: "0.9rem" }}>
              {state.message}
            </div>
          </div>
        </div>
      </>
    );
  }

  const detail = state.detail;

  return (
    <>
      <PageHeader
        title={editable.agentName || "Destination"}
        actions={
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="btn"
              onClick={() => navigate("/destinations")}
              style={{
                background: "#ffffff",
                color: "var(--portal-primary)",
                borderColor: "var(--portal-primary)",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <ArrowLeft size={16} />
              Back
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onSave}
              disabled={!isDirty || saving}
              style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              <Save size={16} />
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        }
      />
      <div className="dashboard">
        {error && (
          <div
            className="tile"
            style={{
              borderColor: "#e74c3c",
              color: "#c0392b",
              background: "#fdecea",
              display: "flex",
              gap: 12,
              alignItems: "flex-start",
            }}
          >
            <AlertTriangle size={18} />
            <div>{error}</div>
          </div>
        )}
        {success && (
          <div
            className="tile"
            style={{
              borderColor: "var(--portal-status-success)",
              color: "var(--portal-status-success-darker)",
              background: "var(--portal-status-success-soft)",
              display: "flex",
              gap: 12,
              alignItems: "flex-start",
            }}
          >
            <CheckCircle2 size={18} />
            <div>{success}</div>
          </div>
        )}

        <section className="tile">
          <h3 style={{ marginTop: 0 }}>General</h3>
          {detail.linkedDnis.length > 0 && (
            <LinkedDnisBanner items={detail.linkedDnis} />
          )}
          <FormRow label="Name">
            <input
              type="text"
              value={editable.agentName}
              onChange={(e) =>
                setEditable({ ...editable, agentName: e.target.value })
              }
              style={inputStyle}
            />
          </FormRow>
        </section>

        <section className="tile">
          <h3 style={{ marginTop: 0 }}>Phone</h3>
          <FormRow label="Status">
            <Select
              value={editable.statusId}
              options={STATUS_OPTIONS}
              onChange={(v) => setEditable({ ...editable, statusId: v })}
            />
          </FormRow>
          <FormRow label="Number">
            <input
              type="text"
              value={editable.destination}
              onChange={(e) =>
                setEditable({ ...editable, destination: e.target.value })
              }
              placeholder="31612345678"
              style={inputStyle}
            />
          </FormRow>
          <FormRow label="Available Numbers">
            <DestinationsBlock
              destinations={detail.destinations}
              activeDestination={editable.destination}
              onAdd={() => setDestOpen(true)}
              onActivate={onActivateDestination}
              onDelete={onDeleteDestination}
            />
          </FormRow>
          <FormRow label="Attempt Timeout (sec)">
            <input
              type="number"
              value={editable.dialTimeout}
              onChange={(e) =>
                setEditable({
                  ...editable,
                  dialTimeout: Number(e.target.value) || 0,
                })
              }
              style={{ ...inputStyle, maxWidth: 180 }}
            />
          </FormRow>
          <FormRow label="Phone display (CLI)">
            <Select
              value={editable.cliTypeId}
              options={CLI_OPTIONS}
              onChange={(v) => setEditable({ ...editable, cliTypeId: v })}
            />
          </FormRow>
          <FormRow label="Max channels">
            <input
              type="number"
              value={editable.maxChannels}
              onChange={(e) =>
                setEditable({
                  ...editable,
                  maxChannels: Number(e.target.value) || 0,
                })
              }
              style={{ ...inputStyle, maxWidth: 180 }}
            />
          </FormRow>
          <FormRow label="Mute whisper">
            <Toggle
              checked={editable.muteWhisper}
              label="No whisper announcement"
              onChange={(v) => setEditable({ ...editable, muteWhisper: v })}
            />
          </FormRow>
        </section>

        <section className="tile">
          <h3 style={{ marginTop: 0 }}>Voicemail</h3>
          <FormRow label="Voicemail">
            <Toggle
              checked={editable.voicemailEnabled}
              label="Enabled"
              onChange={(v) =>
                setEditable({ ...editable, voicemailEnabled: v })
              }
            />
          </FormRow>
          {detail.internalCallsSupported && (
            <FormRow label="Greeting">
              <div style={{ display: "grid", gap: 8, maxWidth: 360 }}>
                <select
                  value={editable.voicemailVoiceId ?? ""}
                  disabled={!editable.voicemailEnabled}
                  aria-label="Voicemail greeting"
                  onChange={(e) => setEditable({ ...editable, voicemailVoiceId: e.target.value ? Number(e.target.value) : null })}
                  style={{ ...inputStyle, paddingRight: 36 }}
                >
                  <option value="">(system prompt)</option>
                  {(voices ?? []).map((v) => (
                    <option key={v.voiceId} value={v.voiceId}>{v.name}</option>
                  ))}
                  {editable.voicemailVoiceId != null && voices && !voices.some((v) => v.voiceId === editable.voicemailVoiceId) && (
                    <option value={editable.voicemailVoiceId} disabled>
                      {detail.voicemailVoiceName ? `${detail.voicemailVoiceName} (no audio file)` : "(previous greeting, no longer available)"}
                    </option>
                  )}
                </select>
                {editable.voicemailEnabled && editable.voicemailVoiceId != null && voices?.some((v) => v.voiceId === editable.voicemailVoiceId) && (
                  <audio controls preload="none" src={voiceAudioUrl(null, editable.voicemailVoiceId)} style={{ width: "100%", height: 32 }} />
                )}
                <div style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>
                  Played before the beep when a caller transferred by a colleague on the PBX reaches this voicemail. Record or upload greetings under Voices.
                </div>
              </div>
            </FormRow>
          )}
          <FormRow label="Notification">
            <Toggle
              checked={editable.voicemailSendEmail}
              label="Send email when a new voicemail arrives"
              onChange={(v) =>
                setEditable({ ...editable, voicemailSendEmail: v })
              }
            />
          </FormRow>
        </section>

        <section className="tile">
          <h3 style={{ marginTop: 0 }}>Recordings</h3>
          <FormRow label="Record calls">
            <Toggle
              checked={editable.recordCalls}
              label="Record all calls"
              onChange={(v) => setEditable({ ...editable, recordCalls: v })}
            />
          </FormRow>
        </section>

        {(detail.customerPbxEnabled || detail.pbxEnabled || editable.pbxEnabled) && (
          <section className="tile">
            <h3 style={{ marginTop: 0 }}>PBX</h3>
            <FormRow label="PBX login">
              <Toggle
                checked={editable.pbxEnabled}
                label="Allow login on the SMX PBX"
                onChange={(v) => setEditable({ ...editable, pbxEnabled: v })}
              />
            </FormRow>
            {detail.internalCallsSupported && (
              <FormRow label="Extension number">
                <div style={{ display: "grid", gap: 6, maxWidth: 360 }}>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="e.g. 0342"
                    value={editable.pbxExtension}
                    aria-label="Extension number"
                    onChange={(e) => setEditable({ ...editable, pbxExtension: e.target.value.replace(/[^0-9]/g, "").slice(0, 4) })}
                    style={{ ...inputStyle, maxWidth: 140, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", letterSpacing: "0.08em" }}
                  />
                  <div style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>
                    4 digits, unique within this customer. Colleagues dial it from their PBX phone and use it to transfer callers; leave empty to assign none.
                  </div>
                </div>
              </FormRow>
            )}
            <FormRow label="Username">
              <ReadOnlyText value={detail.pbxUsername} />
            </FormRow>
            <FormRow label="Password">
              <ReadOnlyText value={detail.pbxPassword} mono />
            </FormRow>
            <FormRow label="Registered">
              <ReadOnlyText
                value={
                  detail.pbxRegistered
                    ? `Yes, from ${detail.pbxClientIP || "?"}`
                    : "No"
                }
              />
            </FormRow>
            <FormRow label="Last registration">
              <ReadOnlyText
                value={
                  detail.mostRecentPbxRegisterUTC
                    ? new Date(detail.mostRecentPbxRegisterUTC).toLocaleString(
                        "en-GB",
                      )
                    : "(never)"
                }
              />
            </FormRow>
            {editable.pbxEnabled && detail.pbxIdentitySupported && (
              <>
                <FormRow label="Identity">
                  <div style={{ display: "grid", gap: 8, maxWidth: 360 }}>
                    <select
                      value={editable.pbxIdentityMode}
                      onChange={(e) => {
                        const mode = e.target.value === "number" || e.target.value === "hidden" || e.target.value === "from" ? e.target.value : "denied";
                        setEditable({
                          ...editable,
                          pbxIdentityMode: mode,
                          pbxIdentityDnisId: mode === "number" || mode === "from" ? editable.pbxIdentityDnisId : null,
                        });
                      }}
                      style={{ ...inputStyle, paddingRight: 36 }}
                    >
                      <option value="denied">Denied (no outbound calls from this extension)</option>
                      <option value="hidden">Hidden (withhold caller id)</option>
                      <option value="number">Number (present one of your numbers)</option>
                      <option value="from">Use From (present the number the phone sends)</option>
                    </select>
                    {editable.pbxIdentityMode !== "denied" && (
                      <select
                        value={editable.pbxIdentityDnisId ?? ""}
                        aria-label={editable.pbxIdentityMode === "hidden" ? "Billing number" : editable.pbxIdentityMode === "from" ? "Fallback number" : "Number to present"}
                        onChange={(e) =>
                          setEditable({ ...editable, pbxIdentityDnisId: e.target.value ? Number(e.target.value) : null })
                        }
                        style={{ ...inputStyle, paddingRight: 36 }}
                      >
                        <option value="">
                          {editable.pbxIdentityMode === "hidden"
                            ? "Select the billing number…"
                            : editable.pbxIdentityMode === "from"
                              ? "Select the fallback number…"
                              : "Select a number…"}
                        </option>
                        {(identityNumbers ?? []).map((n) => (
                          <option key={n.dnisId} value={n.dnisId}>
                            {formatPhoneNumber(n.dnisPresentation) || n.dnisPresentation}
                            {n.description ? ` · ${n.description}` : ""}
                          </option>
                        ))}
                        {editable.pbxIdentityDnisId != null &&
                          identityNumbers &&
                          !identityNumbers.some((n) => n.dnisId === editable.pbxIdentityDnisId) && (
                            <option value={editable.pbxIdentityDnisId} disabled>
                              (previous number, no longer available)
                            </option>
                          )}
                      </select>
                    )}
                    {!detail.pbxIdentityValid && (
                      <div className="alert alert-error" style={{ margin: 0 }}>
                        The stored identity number is no longer an active number of this customer. Choose another number or Hidden, then save.
                      </div>
                    )}
                    {editable.pbxIdentityMode !== "denied" && identityNumbers && identityNumbers.length === 0 && (
                      <div style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>This customer has no active numbers to present.</div>
                    )}
                    <div style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>
                      {editable.pbxIdentityMode === "denied"
                        ? "Outbound calls from this PBX login are blocked."
                        : editable.pbxIdentityMode === "hidden"
                          ? "The callee sees an anonymous call; the chosen number is only passed to the carrier for billing."
                          : editable.pbxIdentityMode === "from"
                            ? "The number in the From of the phone's call is presented when it is one of your numbers; any other phone number is rejected. When the From holds no phone number (anonymous, an extension), the fallback number is used."
                            : "Used as caller id for outbound calls made from this PBX login."}
                    </div>
                  </div>
                </FormRow>
              </>
            )}
          </section>
        )}

        <section className="tile">
          <h3 style={{ marginTop: 0 }}>Contact</h3>
          <FormRow label="Email">
            <input
              type="email"
              value={editable.emailAddress}
              onChange={(e) =>
                setEditable({ ...editable, emailAddress: e.target.value })
              }
              style={inputStyle}
            />
          </FormRow>
          <FormRow label="Mobile (SMS)">
            <input
              type="text"
              value={editable.agentSignoffSMS}
              onChange={(e) =>
                setEditable({
                  ...editable,
                  agentSignoffSMS: e.target.value,
                })
              }
              placeholder="316XXXXXXXX"
              style={inputStyle}
            />
          </FormRow>
        </section>

        <section className="tile">
          <h3 style={{ marginTop: 0 }}>Login</h3>
          <FormRow label="Username">
            <input
              type="text"
              value={editable.username}
              onChange={(e) =>
                setEditable({ ...editable, username: e.target.value })
              }
              style={inputStyle}
            />
          </FormRow>
          <FormRow label="Password">
            <button
              type="button"
              className="btn"
              onClick={() => setPwOpen(true)}
              style={{
                background: "#ffffff",
                color: "var(--portal-primary)",
                borderColor: "var(--portal-primary)",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <KeyRound size={14} />
              Change password
            </button>
          </FormRow>
        </section>

        <section className="tile">
          <h3 style={{ marginTop: 0, color: "#c0392b" }}>Archive</h3>
          <p style={{ color: "var(--portal-muted)" }}>
            Archiving a destination is permanent and cannot be undone.
          </p>
          <button
            type="button"
            className="btn"
            onClick={() => setArchiveConfirmOpen(true)}
            disabled={archiving}
            style={{
              background: "#ffffff",
              color: "#c0392b",
              borderColor: "#e74c3c",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Trash2 size={16} />
            {archiving ? "Archiving…" : "Archive destination"}
          </button>
        </section>
      </div>

      {state.status === "ready" && editable && (
        <ConfirmModal
          open={archiveConfirmOpen}
          onClose={() => {
            if (!archiving) setArchiveConfirmOpen(false);
          }}
          title="Archive destination?"
          confirmLabel="Archive"
          tone="danger"
          busy={archiving}
          onConfirm={() => void performArchive()}
        >
          Are you sure you want to archive destination &quot;
          {editable.agentName || "—"}&quot;? This cannot be undone.
        </ConfirmModal>
      )}

      {pwOpen && (
        <PasswordModal
          onClose={() => setPwOpen(false)}
          onSubmit={onChangePassword}
        />
      )}
      {destOpen && (
        <AddDestinationModal
          onClose={() => setDestOpen(false)}
          onSubmit={onAddDestination}
        />
      )}
    </>
  );
}

function LinkedDnisBanner({
  items,
}: {
  items: AgentDetail["linkedDnis"];
}) {
  return (
    <div
      id="linked-dnis-banner"
      role="status"
      aria-label="Linked numbers warning"
      style={{
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        padding: "12px 14px",
        marginBottom: 16,
        borderRadius: 8,
        border: "1px solid #f1c40f",
        background: "#fffbe6",
        color: "#7a5c00",
      }}
    >
      <AlertTriangle size={18} aria-hidden style={{ marginTop: 2 }} />
      <div style={{ fontSize: "0.9rem", lineHeight: 1.45 }}>
        <strong>
          Linked to{" "}
          {items.map((it, idx) => (
            <span key={it.dnisId}>
              <Link
                to={`/numbers/${it.dnisId}`}
                style={{ color: "inherit", textDecoration: "underline" }}
              >
                {formatPhoneNumber(it.formattedNumber || it.dnisPresentation) ||
                  it.dnisPresentation}
              </Link>
              {idx < items.length - 1 ? ", " : ""}
            </span>
          ))}
          .
        </strong>{" "}
        Changing the dnis prefix in the name will break call routing for{" "}
        {items.length === 1 ? "this number" : "these numbers"}.
      </div>
    </div>
  );
}

function DestinationsBlock({
  destinations,
  activeDestination,
  onAdd,
  onActivate,
  onDelete,
}: {
  destinations: AgentDetail["destinations"];
  activeDestination: string;
  onAdd: () => void;
  onActivate: (id: number) => void;
  onDelete: (id: number) => void;
}) {
  return (
    <div>
      {destinations.length === 0 ? (
        <p style={{ color: "var(--portal-muted)", margin: 0 }}>
          No additional numbers.
        </p>
      ) : (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "0.9rem",
            marginBottom: 12,
          }}
        >
          <thead>
            <tr>
              <Th>Number</Th>
              <Th>Description</Th>
              <Th align="right" />
            </tr>
          </thead>
          <tbody>
            {destinations.map((d) => {
              const isActive = d.destination === activeDestination;
              return (
                <tr
                  key={d.agentDestinationListId}
                  style={{ borderTop: "1px solid var(--portal-border)" }}
                >
                  <Td>
                    <span style={{ fontWeight: 600 }}>{d.destination}</span>
                    {isActive && (
                      <span
                        style={{
                          marginLeft: 8,
                          padding: "1px 8px",
                          borderRadius: 999,
                          background: "var(--portal-status-success-soft)",
                          color: "var(--portal-status-success-darker)",
                          fontSize: "0.7rem",
                          border: "1px solid var(--portal-status-success)",
                        }}
                      >
                        Active
                      </span>
                    )}
                  </Td>
                  <Td>{d.description || "—"}</Td>
                  <Td align="right">
                    <div style={{ display: "inline-flex", gap: 6 }}>
                      {!isActive && (
                        <button
                          type="button"
                          className="btn"
                          onClick={() => onActivate(d.agentDestinationListId)}
                          style={{
                            padding: "4px 10px",
                            fontSize: "0.75rem",
                            background: "#ffffff",
                            color: "var(--portal-primary)",
                            borderColor: "var(--portal-primary)",
                          }}
                        >
                          Activate
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn"
                        onClick={() => onDelete(d.agentDestinationListId)}
                        style={{
                          padding: "4px 10px",
                          fontSize: "0.75rem",
                          background: "#ffffff",
                          color: "#c0392b",
                          borderColor: "#e74c3c",
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <button
        type="button"
        className="btn"
        onClick={onAdd}
        style={{
          background: "#ffffff",
          color: "var(--portal-primary)",
          borderColor: "var(--portal-primary)",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Plus size={14} />
        Add number
      </button>
    </div>
  );
}

function PasswordModal({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (password: string) => void;
}) {
  const [pw, setPw] = useState("");
  return (
    <ModalShell title="Change password" onClose={onClose}>
      <FormRow label="New password">
        <input
          autoFocus
          type="text"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          style={inputStyle}
        />
      </FormRow>
      <ModalFooter
        onCancel={onClose}
        onConfirm={() => onSubmit(pw)}
        confirmDisabled={pw.length === 0}
        confirmLabel="Change"
      />
    </ModalShell>
  );
}

function AddDestinationModal({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (destination: string, description: string) => void;
}) {
  const [dest, setDest] = useState("");
  const [desc, setDesc] = useState("");
  return (
    <ModalShell title="Add number" onClose={onClose}>
      <FormRow label="Number">
        <input
          autoFocus
          type="text"
          value={dest}
          onChange={(e) => setDest(e.target.value)}
          style={inputStyle}
          placeholder="31612345678"
        />
      </FormRow>
      <FormRow label="Description">
        <input
          type="text"
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          style={inputStyle}
        />
      </FormRow>
      <ModalFooter
        onCancel={onClose}
        onConfirm={() => onSubmit(dest, desc)}
        confirmDisabled={dest.trim().length === 0}
        confirmLabel="Add"
      />
    </ModalShell>
  );
}

function ModalShell({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: 12,
          padding: 24,
          minWidth: 480,
          maxWidth: "90vw",
          boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
        }}
      >
        <h3 style={{ marginTop: 0 }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}

function ModalFooter({
  onCancel,
  onConfirm,
  confirmDisabled,
  confirmLabel,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  confirmDisabled: boolean;
  confirmLabel: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 8,
        marginTop: 16,
      }}
    >
      <button type="button" className="btn" onClick={onCancel}>
        Cancel
      </button>
      <button
        type="button"
        className="btn btn-primary"
        onClick={onConfirm}
        disabled={confirmDisabled}
      >
        {confirmLabel}
      </button>
    </div>
  );
}

function FormRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "200px 1fr",
        gap: 16,
        alignItems: "flex-start",
        padding: "8px 0",
      }}
    >
      <label
        style={{
          color: "var(--portal-muted)",
          fontWeight: 600,
          paddingTop: 8,
        }}
      >
        {label}
      </label>
      <div>{children}</div>
    </div>
  );
}

function Th({
  children,
  align,
}: {
  children?: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      style={{
        textAlign: align ?? "left",
        padding: "8px 12px",
        color: "var(--portal-muted)",
        fontWeight: 600,
        fontSize: "0.75rem",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <td
      style={{
        padding: "10px 12px",
        verticalAlign: "middle",
        textAlign: align ?? "left",
      }}
    >
      {children}
    </td>
  );
}

function Select({
  value,
  options,
  onChange,
}: {
  value: number;
  options: { id: number; label: string }[];
  onChange: (next: number) => void;
}) {
  return (
    <div style={{ position: "relative", maxWidth: 360 }}>
      <select
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ ...inputStyle, paddingRight: 36, appearance: "none" }}
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown
        size={16}
        style={{
          position: "absolute",
          right: 12,
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
          color: "var(--portal-muted)",
        }}
      />
    </div>
  );
}

function Toggle({
  checked,
  label,
  onChange,
}: {
  checked: boolean;
  label: string;
  onChange: (next: boolean) => void;
}) {
  return (
    <label
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

function ReadOnlyText({ value, mono }: { value: string; mono?: boolean }) {
  return (
    <div
      style={{
        padding: "8px 12px",
        background: "var(--portal-surface)",
        border: "1px solid var(--portal-border)",
        borderRadius: 6,
        color: "var(--portal-muted)",
        fontFamily: mono
          ? "ui-monospace, SFMono-Regular, Menlo, monospace"
          : "inherit",
        maxWidth: 360,
      }}
    >
      {value || "—"}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 12px",
  border: "1px solid var(--portal-border)",
  borderRadius: 6,
  fontSize: "0.95rem",
  fontFamily: "inherit",
  background: "#ffffff",
  maxWidth: 360,
};
